import { useState, useCallback, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import cn from 'classnames';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers';
import { useForm } from 'react-hook-form';
// Lib
import * as ttq from 'lib/ttpixel';
import * as ga from 'lib/google-analytics';
// Hooks
import { useGetStoreAmas, useGetCurrUser, useConfirmCardPayment } from 'hooks';
// Api
import { CREATE_AMA_REQUEST } from 'api/ama/mutations';
// Types
import {
  CreateAmaRequest,
  CreateAmaRequestVariables,
} from 'api/ama/types/CreateAmaRequest';
import { AmaType, AmaStatus } from 'api/graphql-global-types';
import {
  GetStoreAmas_store,
  GetStoreAmas_store_amas,
} from 'api/ama/types/GetStoreAmas';
import { GetStream_stream_store } from 'api/streams/types/GetStream';
// Helpers
import {
  yupEmailValidation,
  yupStringValidation,
  yupEmailRequiredValidation,
  yupStringRequiredValidation,
} from 'helpers/validation';
import { getUserFullName, mapStreamerRoleToLabel } from 'helpers/user';
import { getStreamerName } from 'helpers/streams';
// Ui
import Button from 'ui3/Button/Button';
import TextField from 'ui3/TextField/TextField';
// Components
import { showToast } from 'components/common/Toast/Toast';
import PaymentMethods, {
  PaymentSubmitProps,
} from 'components/common3/PaymentMethods/PaymentMethods';
// Styles
import styles from './AmaPaymentForm.module.scss';

type AmaPaymentFormProps = {
  paymentRedirectPath: string;
  store?: GetStream_stream_store | GetStoreAmas_store | null;
  theme?: Theme;
  className?: string;
};

type AmaForOption = {
  value: string;
  label: string;
};

type FormInputs = {
  to: string;
  from: string;
  email: string;
  instructions: string;
};

const getValidationSchema = (forSomeone: boolean) =>
  yup.object().shape({
    to: yupStringRequiredValidation('To'),
    from: forSomeone
      ? yupStringRequiredValidation('From')
      : yupStringValidation('From'),
    email: forSomeone ? yupEmailRequiredValidation() : yupEmailValidation(),
    instructions: yupStringRequiredValidation('Text instructions', {
      maxChars: 1000,
    }),
  });

const defaultErrorMessage =
  'Looks like something went wrong. Please double-check all fields and try again';

const AMA_FOR_OPTIONS: AmaForOption[] = [
  {
    value: 'forMyself',
    label: 'For Myself',
  },
  {
    value: 'forSomeone',
    label: 'For Someone',
  },
];

const AmaPaymentForm = ({
  paymentRedirectPath,
  store,
  className,
}: AmaPaymentFormProps) => {
  const [amaFor, setAmaFor] = useState<string>(AMA_FOR_OPTIONS[0].value);
  const [
    isSuccessPaymentModalVisible,
    setSuccessPaymentModalVisibility,
  ] = useState<boolean>(false);

  const [createAmaRequest, { loading: createAmaRequestLoading }] = useMutation<
    CreateAmaRequest,
    CreateAmaRequestVariables
  >(CREATE_AMA_REQUEST);

  const {
    confirmCardPayment,
    loading: stripeLoading,
  } = useConfirmCardPayment();

  const { data: userData } = useGetCurrUser();

  // Use store.amas from props if available, otherwise fetch from API
  const { data: amasData } = useGetStoreAmas({
    variables: {
      input: {
        id: store?.id || '',
      },
    },
    skip: !store?.id || !!(store.amas && store.amas.length > 0),
  });

  const storeAmas =
    store && store.amas && store.amas.length > 0
      ? store.amas
      : amasData?.store?.amas;

  const toggleAmaFor = () => {
    const value =
      amaFor === AMA_FOR_OPTIONS[0].value
        ? AMA_FOR_OPTIONS[1].value
        : AMA_FOR_OPTIONS[0].value;
    setAmaFor(value);
  };

  const showError = (message: string = defaultErrorMessage) => {
    showToast({
      message,
      type: 'error',
    });
  };

  const onSuccessPayment = (price: number) => {
    setSuccessPaymentModalVisibility(true);

    ttq.placeAnOrder({
      content_id: selectedAmaId || '',
      content_type: 'product',
      content_name: store?.slug || '',
      content_category: `Ama (${selectedAma?.type || 'Ama'})`,
      quantity: 1,
      price: price,
      value: price,
      currency: 'USD',
    });

    ga.confirmedPurchase({
      currency: 'USD',
      value: price,
      items: [
        {
          id: selectedAmaId || '',
          name: storeName,
          category: 'Ama',
          brand: storeName,
          quantity: 1,
          price: price,
          variant: selectedAma?.type || 'Ama',
        },
      ],
    });
  };

  // TODO: add some ama type toggle button when Shoutout feature will be turned on
  const selectedAma: GetStoreAmas_store_amas | undefined = storeAmas?.find(
    (ama) => ama.type === AmaType.Ama
  );
  const selectedAmaId = selectedAma?.id || '';
  const selectedAmaPrice = selectedAma?.price || 0;
  const isForSomeOne: boolean = amaFor === AMA_FOR_OPTIONS[1].value;
  const userEmail = userData?.me?.email || '';
  const storeName = getStreamerName(store);
  const userName = getUserFullName(userData);

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    errors: formErrors,
  } = useForm<FormInputs>({
    resolver: yupResolver(getValidationSchema(isForSomeOne)),
  });

  const toFieldValue = getValues('to');
  const fromFieldValue = getValues('from');
  const memoizedSetToFromFieldValues = useCallback(
    (to: string, from: string, shouldValidate: boolean) => {
      setValue('to', to, { shouldValidate });
      setValue('from', from, { shouldValidate });
    },
    [setValue]
  );

  useEffect(() => {
    if (userName.length) {
      const toMyself = toFieldValue?.length ? toFieldValue : userName;
      const toForSomeone =
        (toFieldValue === userName && !fromFieldValue?.length) ||
        !toFieldValue?.length
          ? ''
          : toFieldValue;

      const fromMyself = fromFieldValue?.length ? fromFieldValue : userName;
      const fromForSomeone = fromFieldValue?.length ? fromFieldValue : userName;

      memoizedSetToFromFieldValues(
        isForSomeOne ? toForSomeone : toMyself,
        isForSomeOne ? fromForSomeone : fromMyself,
        !isForSomeOne
      );
    }
  }, [
    userName,
    isForSomeOne,
    toFieldValue,
    fromFieldValue,
    memoizedSetToFromFieldValues,
  ]);

  const handlePayment = ({
    paymentMethodId,
    promoCode,
  }: PaymentSubmitProps) => async ({
    to,
    from,
    email,
    instructions,
  }: FormInputs): Promise<void> => {
    try {
      if (paymentMethodId) {
        const { data } = await createAmaRequest({
          variables: {
            input: {
              promocodeId: promoCode?.id || null,
              amaId: selectedAmaId,
              instructions,
              email: email || userEmail,
              from: isForSomeOne ? from : null,
              to,
            },
          },
        });

        const stripeIntentClientSecret =
          data?.createAmaRequest?.stripeIntentClientSecret;
        const stripeIntentID = data?.createAmaRequest?.stripeIntentID;
        const amaPrice =
          stripeIntentID !== 'promo_free_id' ? selectedAmaPrice : 0;

        if (stripeIntentClientSecret && stripeIntentID) {
          ttq.initiateCheckout({
            content_id: selectedAmaId,
            content_type: 'product',
            content_name: store?.slug || '',
            content_category: `Ama (${selectedAma?.type || ''})`,
            quantity: 1,
            price: amaPrice,
            value: amaPrice,
            currency: 'USD',
          });

          ga.checkout({
            currency: 'USD',
            items: [
              {
                id: selectedAmaId,
                name: storeName,
                category: 'Ama',
                brand: storeName,
                quantity: 1,
                price: amaPrice,
                variant: selectedAma?.type || '',
              },
            ],
            value: amaPrice,
          });

          if (stripeIntentID !== 'promo_free_id') {
            await confirmCardPayment({
              stripeIntentClientSecret,
              paymentMethodId,
              onErrorCallback: showError,
              onSuccessCallback: () => onSuccessPayment(amaPrice),
            });
          } else {
            onSuccessPayment(amaPrice);
          }
        } else {
          showError();
        }
      }
    } catch (err) {
      showError();
    }
  };

  const handlePaymentSubmit = (props: PaymentSubmitProps) => {
    handleSubmit(handlePayment(props))();
  };

  const isCtaLoading = createAmaRequestLoading || stripeLoading;
  const hasNoActiveAma = !storeAmas?.some(
    (item) => item.status === AmaStatus.Active
  );

  if (!store || hasNoActiveAma) {
    return null;
  }

  return (
    <>
      <form className={cn(styles.root, className)} aria-label="AMA payment">
        <div className={styles.actionHeader}>
          {AMA_FOR_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant={option.value === amaFor ? 'primary' : 'secondary'}
              onClick={toggleAmaFor}
              type="button"
            >
              {option.label}
            </Button>
          ))}
        </div>

        <div className={styles.actionBody}>
          <div className={styles.actionBodySection}>
            <TextField
              name="to"
              type="text"
              ref={register}
              placeholder={`Type name of ${
                isForSomeOne ? 'recipient' : 'receiver'
              }`}
              error={formErrors?.to?.message}
              label="To"
            />

            {isForSomeOne && (
              <>
                <TextField
                  name="from"
                  type="text"
                  ref={register}
                  placeholder="Type your name"
                  error={formErrors?.from?.message}
                  label="From"
                />

                <TextField
                  name="email"
                  type="email"
                  ref={register}
                  error={formErrors?.email?.message}
                  placeholder="Type email"
                  label="Email of recipient"
                />
              </>
            )}

            <TextField
              name="instructions"
              ref={register}
              error={formErrors?.instructions?.message}
              placeholder="Type your question or message here"
              type="textarea"
              label="Text instruction"
            />
          </div>
        </div>

        <PaymentMethods
          amaId={selectedAmaId}
          successPaymentModalTitle="Thank you for your order"
          goToLabel={`Go to ${mapStreamerRoleToLabel(store.role)} page`}
          successPaymentModalDescription={
            <>
              You&apos;ve successfully ordered a personal video from{' '}
              <strong>{storeName}</strong>. {store.firstName} usually responds
              withing <strong>{selectedAma?.daysToResponse} days</strong>. You
              will receive an email with your personal video when it is ready.
              You will also be able to access this video via the Purchase
              section of your account.
            </>
          }
          isSuccessPaymentModalVisible={isSuccessPaymentModalVisible}
          setSuccessPaymentModalVisibility={setSuccessPaymentModalVisibility}
          paymentRedirectPath={paymentRedirectPath}
          loading={isCtaLoading}
          price={selectedAmaPrice}
          onSubmit={handlePaymentSubmit}
        />
      </form>
    </>
  );
};

export default AmaPaymentForm;
